/**
 * Hook que mantiene una conexión WebSocket viva con reconnect exponencial y
 * dispatchea eventos del server al cache de TanStack Query.
 *
 * Implementado con WebSocket nativo del browser. partysocket cambió su API en
 * v1 a un esquema de "parties/rooms" de PartyKit que no encaja con un endpoint
 * WS genérico — preferimos no traer esa dep.
 *
 * Diseño:
 * - Reconnect: backoff exponencial 1s → 30s con jitter.
 * - Heartbeat: el server pinguea cada 25s y nosotros respondemos con pong.
 *   Si el socket se cae el browser dispara onclose y reintentamos.
 * - El cliente NO envía mensajes de chat por WS (eso va por REST POST).
 */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";
import { useWSStore } from "@/stores/ws";
import type { Message } from "@/api/messages";

type ServerMessage =
  | { type: "ping" }
  | { type: "message.created"; data: Message }
  | { type: "message.updated"; data: { id: number; room_id: string; content_text: string } }
  | { type: "message.deleted"; data: { id: number; room_id: string } }
  | { type: "message.read"; data: { user_id: string; up_to_message_id: number } }
  | { type: "user.typing"; data: { user_id: string; room_id: string } }
  | { type: "tenant.status_changed"; data: { status: string } };

let notifPermissionRequested = false;

function maybeNotify(m: Message) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (!document.hidden) return; // solo si la tab no está visible
  const myId = useAuthStore.getState().me?.id;
  if (myId && m.sender_user_id === myId) return;
  if (Notification.permission === "default" && !notifPermissionRequested) {
    notifPermissionRequested = true;
    Notification.requestPermission();
    return;
  }
  if (Notification.permission !== "granted") return;
  const preview = m.content_text?.slice(0, 100) ?? "Nuevo mensaje";
  const n = new Notification("Datasup — nuevo mensaje", {
    body: preview,
    tag: `msg-${m.room_id}`,
  });
  n.onclick = () => {
    window.focus();
    window.location.assign(`/chat/${m.room_id}`);
    n.close();
  };
}

function buildWsUrl(token: string): string {
  const wsBase = import.meta.env.VITE_WS_URL || "/ws";
  if (wsBase.startsWith("ws://") || wsBase.startsWith("wss://")) {
    return `${wsBase}?token=${encodeURIComponent(token)}`;
  }
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}${wsBase}?token=${encodeURIComponent(token)}`;
}

export function useWS() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setStatus = useWSStore((s) => s.setStatus);
  const setTyping = useWSStore((s) => s.setTyping);
  const setSendTyping = useWSStore((s) => s.setSendTyping);
  const logout = useAuthStore((s) => s.logout);
  const qc = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const closedByUsRef = useRef(false);

  useEffect(() => {
    if (!accessToken) {
      setStatus("idle");
      return;
    }
    closedByUsRef.current = false;

    function connect() {
      setStatus(retryRef.current === 0 ? "connecting" : "reconnecting");
      const url = buildWsUrl(accessToken!);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.addEventListener("open", () => {
        retryRef.current = 0;
        setStatus("connected");
        // Inyectamos el sender de typing al store global.
        setSendTyping((roomId: string) => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "typing", room_id: roomId }));
          }
        });
      });

      ws.addEventListener("close", () => {
        wsRef.current = null;
        if (closedByUsRef.current) return;
        // Backoff exponencial con jitter, capeado a 30s.
        const base = Math.min(30_000, 500 * 2 ** retryRef.current);
        const delay = base / 2 + Math.random() * (base / 2);
        retryRef.current += 1;
        setStatus("reconnecting");
        reconnectTimerRef.current = window.setTimeout(connect, delay);
      });

      ws.addEventListener("error", () => {
        // El close handler hace el reintento.
      });

      ws.addEventListener("message", (ev: MessageEvent) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        switch (msg.type) {
          case "ping":
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: "pong" }));
            }
            break;
          case "message.created": {
            const m = msg.data;
            qc.setQueryData<Message[] | undefined>(["messages", m.room_id], (old) => {
              const list = old ?? [];
              const replacedIdx = list.findIndex((x) => x.client_msg_uuid === m.client_msg_uuid);
              if (replacedIdx >= 0) {
                const copy = [...list];
                copy[replacedIdx] = { ...m, _status: "sent" };
                return copy;
              }
              if (list.some((x) => x.id === m.id)) return list;
              // Notif del browser si la tab no está visible y el mensaje no es propio.
              maybeNotify(m);
              return [...list, { ...m, _status: "sent" }];
            });
            // Refresh sidebar para actualizar preview/unread.
            qc.invalidateQueries({ queryKey: ["my-rooms"] });
            break;
          }
          case "message.updated": {
            const u = msg.data;
            qc.setQueryData<Message[] | undefined>(["messages", u.room_id], (old) =>
              (old ?? []).map((m) => (m.id === u.id ? { ...m, content_text: u.content_text } : m)),
            );
            break;
          }
          case "message.deleted": {
            const d = msg.data;
            qc.setQueryData<Message[] | undefined>(["messages", d.room_id], (old) =>
              (old ?? []).map((m) =>
                m.id === d.id ? { ...m, deleted_at: new Date().toISOString() } : m,
              ),
            );
            break;
          }
          case "user.typing": {
            setTyping(msg.data.room_id, msg.data.user_id);
            break;
          }
          case "tenant.status_changed": {
            if (msg.data.status === "suspended" || msg.data.status === "deleted") {
              logout();
            } else {
              qc.invalidateQueries({ queryKey: ["me"] });
            }
            break;
          }
          default:
            break;
        }
      });
    }

    connect();

    return () => {
      closedByUsRef.current = true;
      if (reconnectTimerRef.current != null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setSendTyping(null);
      setStatus("closed");
    };
  }, [accessToken, qc, setStatus, setTyping, setSendTyping, logout]);
}
